import {
  Component,
  forwardRef,
  Input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, ControlValueAccessor } from '@angular/forms';
import {
  DocumentReference,
  DocumentService,
} from '../../../documents/documents.service';
import { SharedModule } from '../../shared.module';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-material-chunk-file-uploader',
  templateUrl: './material-chunk-file-upload.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => MaterialChunkFileUploadComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => MaterialChunkFileUploadComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SharedModule, MatIconModule, MatInputModule]
})
export class MaterialChunkFileUploadComponent implements ControlValueAccessor, Validator {

  currentFiles?: FileList;
  _accept?: string;
  _disabled?: boolean;
  _fileSize: number = 5000000;
  _valid = false;
  fileName?: string;
  _label = '';
  isDragging: boolean = false;
  _drag = false;
  _id?: string;

  onChange = (_value: DocumentReference | undefined) => { };
  onTouched = () => { };

  @Input() set accept(value: string) {
    this._accept = value;
  }

  @Input() set disabled(value: boolean) {
    this._disabled = value;
  }

  @Input() set fileSize(value: number) {
    this._fileSize = value;
  }

  @Input() required = false;

  @Input() set label(value: string) {
    this._label = value;
  }

  @Input() set id(value: string) {
    this._id = value;
  }

  constructor(private _documentService: DocumentService) { }

  onDragOver(event: DragEvent): void {
    if (this._disabled) { return; }
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    if (this._disabled) { return; }
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onFileDrop(event: DragEvent): void {
    if (this._disabled) { return; }
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFiles(files);
    }
  }

  processFiles(fileList: FileList): void {
    if (!fileList || fileList.length === 0) { return; }
    const fileToUpload: File = fileList.item(0)!;
    this.fileName = fileToUpload.name;
    this._valid = true;
    this.currentFiles = fileList;
    this._documentService
      .upload(fileToUpload)
      .then((results) => {
        this.onChange(results);
      });
  }

  valueChanged(fileEvent: any) {
    if (fileEvent.target.files) {
      const fileToUpload: any = fileEvent.target.files.item(0);
      if (fileToUpload.size >= this._fileSize) {
        this.fileName = undefined;
        this.currentFiles = undefined;
        this.onChange(undefined);
        confirm(`File should be smaller than ${this._fileSize / 1000000}MB`);
      } else {
        this.currentFiles = fileEvent.target.files;
        this.fileName = fileEvent.target.files[0].name;
        this._documentService
          .upload(fileToUpload)
          .then((results) => {
            this.onChange(results);
          });
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: any): void {
    this.fileName = value || '';
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.required && !this.fileName) {
      return { required: true };
    }
    return null;
  }
}
